import type { SupplierSettingsMessage } from '../../../../types/SupplierSettingsMessage';
import { Button } from '../../../cdl/Button/Button';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { Text } from '../../../cdl/Text/Text';
import { Tooltip } from '../../../cdl/Tooltip/Tooltip';
import { DataSheet } from '../../../common/DataSheet/DataSheet';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { Box } from '../../../common/ui/Box';

import { MessageSettingsForm } from './MessageSettingsForm';
import { useSupplierSettingsEdit } from './useSupplierSettingsEdit';

interface SupplierMessageSettingsProps {
    supplierSettings: SupplierSettingsMessage;
    supplierId: string;
}

export const SupplierMessageSettings = ({ supplierSettings, supplierId }: SupplierMessageSettingsProps) => {
    const { push, pop } = useDrawer();
    const updateSupplierSettingsMutation = useSupplierSettingsEdit();

    const openMessageSettings = () => {
        const onSubmit = async (values: SupplierSettingsMessage) => {
            try {
                await updateSupplierSettingsMutation.mutateAsync({ id: supplierId, settings: values });
                pop(drawerId);
            } catch {
                return;
            }
        };

        const drawerId = push({
            header: { title: translate('supplier.messageSettings.editMessageSettings') },
            content: <MessageSettingsForm supplierSettingsData={supplierSettings} onSubmit={onSubmit} />,
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={16}>
                <Box display="flex" alignItems="center" gap={6}>
                    <Text variant="subtitle1" as="h2">
                        {translate('supplier.messageSettings.headline')}
                    </Text>
                    <Tooltip label={translate('supplier.messageSettings.explanation')}>
                        <span>
                            <IconInfoCircle width={16} height={16} />
                        </span>
                    </Tooltip>
                </Box>
                <Button onClick={openMessageSettings}>{translate('actions.edit')}</Button>
            </Box>

            <DataSheet>
                <DataSheet.Label>{translate('supplier.messageSettings.quotationMessageLabel')}</DataSheet.Label>
                <Text>{supplierSettings.messageSettings.quotationMessage || Characters.EN_DASH}</Text>

                <DataSheet.Label>{translate('supplier.messageSettings.orderConfirmationMessageLabel')}</DataSheet.Label>
                <Text>{supplierSettings.messageSettings.orderConfirmationMessage || Characters.EN_DASH}</Text>
            </DataSheet>
        </Box>
    );
};
